import { ClientConfig, ProjectTemplateColumnDef, ProjectTemplateColumnDefId, ProjectTemplateColumnDefIds, SequenceCode, WorldData } from '../models/world';
import { getCookie } from './common';

export type ApiEndpoint = string;

export const API_ENDPOINTS: {
  OLIGO_CONTENT: ApiEndpoint;
  OLIGO_VALIDATE: ApiEndpoint;
  OLIGO_DEFAULT: ApiEndpoint;
  GET_PRICE: ApiEndpoint;
  GET_USER_DETAILS: ApiEndpoint;
  GET_CART_PAYLOAD: ApiEndpoint;
  ADD_TO_CART: ApiEndpoint;
  ADD_TO_CART_COMMERCE: ApiEndpoint;
  VIEW_CART: ApiEndpoint;
  BULK_UPLOAD: ApiEndpoint;
  BULK_UPLOAD_OVERRIDE: ApiEndpoint;
  BULK_TEMPLATE_CONTENT: ApiEndpoint;
} = {
  OLIGO_CONTENT: 'content',
  OLIGO_VALIDATE: 'validate',
  OLIGO_DEFAULT: 'default',
  GET_PRICE: 'pricing',
  GET_USER_DETAILS: 'userDetails',
  GET_CART_PAYLOAD: 'addToCartPayload',
  ADD_TO_CART: 'addToCart',
  ADD_TO_CART_COMMERCE: 'addToCartCommerce',
  VIEW_CART: 'viewCart',
  BULK_UPLOAD: 'bulkUpload',
  BULK_UPLOAD_OVERRIDE: 'bulkUploadOverride',
  BULK_TEMPLATE_CONTENT: 'bulkTemplateContent',
};

export type PathConfig = {
  path: string;
  name: string;
  hash: string;
};

export type UriConfig = {
  contextPath: string;
  api: {
    oligo: {
      content: string;
      validate: string;
      default: string;
      addToCart: string;
      addToCartPayload: string;
      bulkUpload: string;
      bulkUploadOverride: string;
      bulkTemplate: string;
      bulkTemplateContent: string;
    };
    pricing: string;
    userDetails: string;
    viewCart: string;
    logout: string;
    login: string;
    addToCartCommerce: string;
  };
  href: {
    standardOligo: {
      bulk: string;
    };
    valueOligo: {
      single: string;
    };
    plateOligo: {
      single: string;
    };
  },
};

export interface AppConfigService {
  load(config: PathConfig): ng.IPromise<UriConfig>;
  setUriConfig(config: UriConfig): void;
  pathFor(endpoint: ApiEndpoint): string;
  getCookieFor(cname: string): string;
  getRuleVersion(): ng.IPromise<string>;
  getAppContent(langCode: string): ng.IPromise<WorldData>;
}

type GetRuleVersionResponse = {
  ruleVersion: string;
};

type GetI18NTextResponse = {
  i18nTextMap: { [key: string]: string };
};

type MixedBase = {
  base: string;
  code: string;
};

type GetMixedBasesResponse = {
  mixedBases: MixedBase[];
};

type TemplateColumnDef = {
  id: ProjectTemplateColumnDefId;
  columnNo: number;
  name: string;
  i18nKey: string;
};

type TemplateImportSettings = {
  maxNoOfItems: number;
  supportedFileExtensions: string[];
  columns: TemplateColumnDef[];
};

type GetTemplateImportSettingsResponse = {
  templateImportSettings: TemplateImportSettings;
};

type GetAppClientConfig = {
  appClientConfig: string | ClientConfig;
};

type GetContentResponse = GetRuleVersionResponse & GetI18NTextResponse & GetMixedBasesResponse & GetTemplateImportSettingsResponse & GetAppClientConfig;

const DEFAULT_TEMPLATE_COLUMN_HEADER_NAMES: {[key: string]: string } = {
  [ProjectTemplateColumnDefIds.OLIGO_VALUE]: 'sequence',
  [ProjectTemplateColumnDefIds.OLIGO_NAME]: 'sequence name',
  [ProjectTemplateColumnDefIds.RESEARCHER_NAME]:  'researcher name',
  [ProjectTemplateColumnDefIds.SCALE]: 'scale',
  [ProjectTemplateColumnDefIds.PRIME_5_MOD]: `5' mod`,
  [ProjectTemplateColumnDefIds.PRIME_3_MOD]: `3' mod`,
  [ProjectTemplateColumnDefIds.PURIFICATION]: 'purification',
  [ProjectTemplateColumnDefIds.SPECIAL_HANDLING]: 'special handling',
};

export class AppConfigServiceImpl implements AppConfigService {

  public getCookieFor = getCookie;
  private uriConfig?: UriConfig;
  private worldData?: WorldData;

  constructor(private $q: ng.IQService, private $http: ng.IHttpService) {
  }

  public load(config: PathConfig): ng.IPromise<UriConfig> {
    const deferred: ng.IDeferred<UriConfig> = this.$q.defer();
    this.$http.get<UriConfig>(`${config.path}/${config.name}?id=${config.hash}`).then((response: ng.IHttpResponse<UriConfig>) => {
      const { status, data } = response;
      if (status === 200) {
        deferred.resolve(data);
      } else {
        deferred.reject(response);
      }
    }, deferred.reject);
    return deferred.promise;
  }

  public setUriConfig(uriConfig: UriConfig): void {
    this.uriConfig = uriConfig;
  }

  public pathFor(endpoint: ApiEndpoint): string {
    let path: string = '';
    const uriConfig: UriConfig = this.uriConfig as UriConfig;
    switch (endpoint) {
      case API_ENDPOINTS.OLIGO_CONTENT:
      case API_ENDPOINTS.OLIGO_DEFAULT:
      case API_ENDPOINTS.OLIGO_VALIDATE:
      case API_ENDPOINTS.BULK_UPLOAD:
      case API_ENDPOINTS.BULK_UPLOAD_OVERRIDE:
      case API_ENDPOINTS.BULK_TEMPLATE_CONTENT:
      case API_ENDPOINTS.ADD_TO_CART:
      case API_ENDPOINTS.GET_CART_PAYLOAD:
        path = `${uriConfig.contextPath}${uriConfig.api.oligo[endpoint]}`;
        break;
      case API_ENDPOINTS.GET_PRICE:
      case API_ENDPOINTS.GET_USER_DETAILS:
      case API_ENDPOINTS.VIEW_CART:
      case API_ENDPOINTS.ADD_TO_CART_COMMERCE:
        path = `${uriConfig.api[endpoint]}`;
        break;
      default:
        break;
    }
    return path;
  }

  public getRuleVersion(): ng.IPromise<string> {
    const path: string = `${this.pathFor(API_ENDPOINTS.OLIGO_CONTENT)}?key=ruleVersion`;
    const deferred: ng.IDeferred<string> = this.$q.defer();
    this.$http.get<GetRuleVersionResponse>(path, { responseType: 'json' })
      .then((response: ng.IHttpResponse<GetRuleVersionResponse>) => {
        const { data, status } = response;
        if (status === 200) {
          deferred.resolve(data.ruleVersion);
        } else {
          deferred.reject();
        }
      }, deferred.reject);
    return deferred.promise;
  }

  public getAppContent(langCode: string): ng.IPromise<WorldData> {
    let { worldData } = this;
    if (worldData) {
      return this.$q.resolve({ ...worldData });
    }
    const language: string = langCode;
    const path: string = `${this.pathFor(API_ENDPOINTS.OLIGO_CONTENT)}`;
    const deferred: ng.IDeferred<WorldData> = this.$q.defer();
    this.$http.get<GetContentResponse>(path, { responseType: 'json' })
      .then((response: ng.IHttpResponse<GetContentResponse>) => {
        const { data, status } = response;
        if (status === 200) {
          const { ruleVersion, mixedBases, i18nTextMap: vocabulary, templateImportSettings, appClientConfig } = data;
          const { maxNoOfItems: maxNoOfOligos, supportedFileExtensions: templateSupportedExtensions, columns } = templateImportSettings;
          const clientConfig: ClientConfig = typeof appClientConfig === 'object' ? appClientConfig : JSON.parse(appClientConfig);
          worldData = {
            ruleVersion,
            vocabulary,
            language,
            clientConfig,
            sequenceCodes: mixedBases.map((mixedBase: MixedBase): SequenceCode => {
              return {
                iupac: mixedBase.code,
                name: mixedBase.base,
              };
            }),
            maxNoOfOligos,
            templateSupportedExtensions,
            templateColumnDefs: columns.map((columnDef: TemplateColumnDef): ProjectTemplateColumnDef => {
              const { id, columnNo: index, name, i18nKey } = columnDef;
              const standardName: string = name || DEFAULT_TEMPLATE_COLUMN_HEADER_NAMES[id];
              return {
                id,
                index,
                standardName,
                name: vocabulary[i18nKey] || standardName,
              };
            }),
          };
          this.worldData = worldData;
          deferred.resolve(worldData);
        } else {
          deferred.reject();
        }
      }, deferred.reject);
    return deferred.promise;
  }

}

export const APP_CONFIG_SERVICE_NAME = 'configService';
export const AppConfigServiceFactory = ($q: ng.IQService, $http: ng.IHttpService): AppConfigService => {
  return new AppConfigServiceImpl($q, $http);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/app-config-service.ts